import { Link } from 'react-router-dom';
import CertificationLayout from '../../layouts/CertificationLayout';
import type { TocItem } from '../../layouts/GuideLayout';
import type { ExamDetails } from '../../layouts/CertificationLayout';
import { AffiliateCTA } from '../../components/content/AffiliateCTA';
import { ResourceLink } from '../../components/content/ResourceLink';
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed';

const examDetails: ExamDetails = {
  examCode: 'AZ-900',
  examName: 'Microsoft Azure Fundamentals',
  passingScore: 700,
  examCostGBP: 100,
  skillsMeasured: [
    'Cloud concepts (25–30%)',
    'Azure architecture and services (35–40%)',
    'Azure management and governance (30–35%)',
  ],
};

const tocItems: TocItem[] = [
  { id: 'what-is-az-900', label: 'What is AZ-900?', level: 2 },
  { id: 'who-should-take', label: 'Who Should Take AZ-900?', level: 2 },
  { id: 'exam-topics', label: 'Exam Topic Breakdown', level: 2 },
  { id: 'uk-context', label: 'UK Context', level: 2 },
  { id: 'free-resources', label: 'Free Study Resources', level: 2 },
  { id: 'paid-resources', label: 'Paid Study Resources', level: 2 },
  { id: 'study-plan', label: 'Recommended Study Plan', level: 2 },
  { id: 'pricing-exam', label: 'Azure Pricing in the Exam', level: 2 },
  { id: 'after-az-900', label: 'After AZ-900 — What Next?', level: 2 },
  { id: 'related-resources', label: 'Related Resources', level: 2 },
];

const topCta = (
  <AffiliateCTA
    programme="Pluralsight"
    courseName="AZ-900 Microsoft Azure Fundamentals"
    url="https://www.pluralsight.com/paths/az-900-microsoft-azure-fundamentals"
    price="From £29/month"
    badgeText="Best Video Course"
  />
);

const bottomCta = (
  <AffiliateCTA
    programme="MeasureUp"
    courseName="AZ-900 Official Practice Test"
    url="https://www.measureup.com/microsoft-practice-test-az-900-microsoft-azure-fundamentals.html"
    price="From £79"
    badgeText="Official Microsoft Partner"
  />
);

export default function AZ900StudyGuidePage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'AZ-900 Study Guide UK 2026',
            description:
              'Complete AZ-900 Microsoft Azure Fundamentals study guide for UK engineers.',
            author: { '@type': 'Organization', name: 'AzureCalc.uk' },
            publisher: {
              '@type': 'Organization',
              name: 'AzureCalc.uk',
              url: 'https://www.azure-calc.co.uk',
            },
            dateModified: '2026-03-30',
            url: 'https://www.azure-calc.co.uk/certifications/az-900-study-guide-uk',
          }),
        }}
      />
      <CertificationLayout
        title="AZ-900 Study Guide UK 2026"
        description="Complete AZ-900 Microsoft Azure Fundamentals study guide for UK engineers 2026. Exam structure, all topic domains, free and paid resources, UK exam booking guide. The best starting point for Azure certification."
        lastUpdated="March 2026"
        tocItems={tocItems}
        examDetails={examDetails}
        relatedCalculators={[
          { service: 'blob-storage', path: '/blob-storage', label: 'Blob Storage Calculator' },
          { service: 'functions', path: '/functions', label: 'Functions Calculator' },
          { service: 'app-service', path: '/app-service', label: 'App Service Calculator' },
        ]}
        topCta={topCta}
        bottomCta={bottomCta}
        canonical="https://www.azure-calc.co.uk/certifications/az-900-study-guide-uk"
        metaTitle="AZ-900 Study Guide UK 2026 | AzureCalc.uk"
        metaDescription="Complete AZ-900 Microsoft Azure Fundamentals study guide for UK engineers. Exam structure, all topic domains, free and paid resources."
      >
        {/* SECTION 1 */}
        <section id="what-is-az-900">
          <h2>What is AZ-900?</h2>
          <p>
            AZ-900 is Microsoft's entry-level Azure certification, validating foundational knowledge
            of cloud concepts and Azure services. It is designed for anyone who wants to demonstrate
            that they understand what Azure is, how it is priced, and where it fits in modern IT
            — without requiring hands-on technical experience or a developer background.
          </p>
          <p>
            There are no technical prerequisites for AZ-900. It is suitable for business analysts,
            project managers, IT support staff, developers new to cloud, and anyone beginning their
            Azure certification journey. The exam consists of 40–60 questions including multiple
            choice, drag-and-drop, and yes/no scenarios. The passing score is 700/1000, and the
            cost is £100 — the cheapest Microsoft certification available.
          </p>
          <p>
            AZ-900 is the most popular Azure certification in the UK by exam volume. It is widely
            required for client site access at major UK consultancies including Accenture, Capgemini,
            KPMG, and Deloitte. While the credential has no formal expiry date, it is worth
            reviewing your knowledge annually as Azure evolves and new services become exam-relevant.
          </p>
        </section>

        {/* SECTION 2 */}
        <section id="who-should-take">
          <h2>Who Should Take AZ-900?</h2>
          <p>AZ-900 is the right certification if you fall into any of these groups:</p>
          <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
            <div className="border rounded p-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
              <p className="text-[13px] font-bold text-[#0078d4] uppercase tracking-wide mb-2">
                Perfect for
              </p>
              <ul className="flex flex-col gap-2">
                {[
                  'IT professionals moving into cloud roles',
                  'Business analysts working with Azure teams',
                  'Project managers overseeing Azure projects',
                  'Developers seeking Azure fundamentals before specialising',
                  'Anyone required by their employer to hold basic Azure certification',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                    <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border rounded p-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
              <p className="text-[13px] font-bold text-[#0078d4] uppercase tracking-wide mb-2">
                Stepping stone to
              </p>
              <ul className="flex flex-col gap-2">
                {[
                  'AZ-104 — Azure Administrator (operations)',
                  'AZ-204 — Azure Developer Associate (development)',
                  'AZ-305 — Solutions Architect (architecture)',
                  'SC-200 — Security Operations Analyst (security)',
                  'AZ-500 — Azure Security Engineer (security)',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                    <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* SECTION 3 */}
        <section id="exam-topics">
          <h2>Exam Topic Breakdown</h2>
          <p>
            AZ-900 covers three domains. Unlike specialist exams, the content is broad rather than
            deep — you need to know what each service is and when to use it, not how to configure it
            in detail.
          </p>
          <div className="not-prose flex flex-col gap-4 my-6">
            {/* Domain 1 */}
            <div className="border rounded p-5" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[14px] font-bold" style={{ color: 'var(--text-primary)' }}>
                  Domain 1 — Cloud Concepts
                </p>
                <span className="text-[12px] font-semibold text-[#0078d4] px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid #0078d4' }}>
                  25–30%
                </span>
              </div>
              <ul className="flex flex-col gap-1.5">
                {[
                  'Benefits of cloud: scalability, reliability, security, governance, manageability',
                  'Cloud service types: IaaS, PaaS, SaaS',
                  'Shared responsibility model',
                  'Cloud models: public, private, hybrid',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                    <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Domain 2 */}
            <div className="border rounded p-5" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[14px] font-bold" style={{ color: 'var(--text-primary)' }}>
                  Domain 2 — Azure Architecture and Services
                </p>
                <span className="text-[12px] font-semibold text-[#0078d4] px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid #0078d4' }}>
                  35–40%
                </span>
              </div>
              <ul className="flex flex-col gap-1.5">
                {[
                  'Core compute: VMs, containers, Azure Functions, App Service',
                  'Azure storage: Blob, Files, Queues, Tables',
                  'Azure networking: VNet, VPN Gateway, ExpressRoute, DNS, Load Balancer',
                  'Azure identity: Microsoft Entra ID, MFA, RBAC',
                  'Azure monitoring: Monitor, Log Analytics, Advisor, Service Health',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                    <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Domain 3 */}
            <div className="border rounded p-5" style={{ backgroundColor: 'var(--surface-container)', borderColor: '#0078d4' }}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[14px] font-bold" style={{ color: 'var(--text-primary)' }}>
                  Domain 3 — Azure Management and Governance
                </p>
                <span className="text-[12px] font-semibold text-[#0078d4] px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid #0078d4' }}>
                  30–35%
                </span>
              </div>
              <ul className="flex flex-col gap-1.5">
                {[
                  'Cost management and Azure pricing models',
                  'Azure subscriptions and management groups',
                  'Azure Policy and Blueprints',
                  'Azure Arc',
                  'Service Level Agreements (SLAs)',
                  'Azure support plans',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                    <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--outline-variant)' }}>
                <p className="text-[12px] italic" style={{ color: '#c7e0f4' }}>
                  Domain 3 covers Azure pricing directly — this is where AzureCalc.uk calculators
                  are directly relevant study material.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4 */}
        <section id="uk-context">
          <h2>UK Context</h2>
          <p>
            At £100, AZ-900 is the cheapest Microsoft certification available and the most commonly
            reimbursed by UK employers as continuing professional development (CPD). Many UK
            organisations have made it a baseline requirement rather than an optional credential.
          </p>
          <p>
            Major UK consulting firms — including the Big 4 (Deloitte, KPMG, EY, PwC) and large
            system integrators — require all cloud-facing staff to hold AZ-900 before working on
            client sites. In the UK public sector, organisations including NHS Digital, HMRC, DVLA,
            and the Cabinet Office operate significant Azure estates; AZ-900 is frequently required
            for contractors and permanent staff on these programmes.
          </p>
          <p>
            Pearson VUE test centres are available across the UK — London, Manchester, Birmingham,
            Leeds, Bristol, Edinburgh, Cardiff, and Belfast — and online proctored sittings are
            available from home for candidates who prefer not to travel.
          </p>
        </section>

        {/* SECTION 5 */}
        <section id="free-resources">
          <h2>Free Study Resources</h2>
          <p>
            AZ-900 is one of the few Microsoft certifications that is genuinely passable using free
            resources alone. The official Microsoft Learn path plus the free practice assessment is
            sufficient for the majority of candidates.
          </p>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              {
                label: 'Microsoft Learn — Official AZ-900 learning path',
                detail: '6 modules, approximately 10 hours total',
              },
              {
                label: 'AZ-900 free practice assessment',
                detail: '50 questions, available directly on Microsoft Learn at no cost',
              },
              {
                label: 'AZ-900 study guide PDF',
                detail: 'Official Microsoft document listing all exam objectives',
              },
              {
                label: 'Adam Docherty — AZ-900 YouTube series',
                detail: 'Well-regarded free video course',
              },
              {
                label: 'FreeCodeCamp AZ-900 full course (YouTube)',
                detail: '3+ hours, highly rated by UK candidates',
              },
              {
                label: 'A Cloud Guru free tier',
                detail: 'Limited free access to AZ-900 content',
              },
            ].map((r) => (
              <div
                key={r.label}
                className="flex items-start gap-3 px-4 py-3 border rounded"
                style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}
              >
                <span className="material-symbols-outlined text-[#0078d4] text-[18px] mt-0.5 shrink-0">
                  check_circle
                </span>
                <div>
                  <p className="text-[13px] font-semibold" style={{ color: 'var(--text-secondary)' }}>{r.label}</p>
                  <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{r.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* SECTION 6 */}
        <section id="paid-resources">
          <h2>Paid Study Resources</h2>
          <p>
            AZ-900 is passable with free resources alone. Paid resources are recommended if you
            want a structured programme with video explanations, want practice tests beyond the
            official 50-question assessment, or want to maximise your score.
          </p>
          <div className="not-prose flex flex-col gap-4 my-6">
            <AffiliateCTA
              programme="Pluralsight"
              courseName="AZ-900 Microsoft Azure Fundamentals"
              url="https://www.pluralsight.com/paths/az-900-microsoft-azure-fundamentals"
              price="From £29/month"
              badgeText="Best Video Course"
            />
            <AffiliateCTA
              programme="MeasureUp"
              courseName="AZ-900 Official Practice Test"
              url="https://www.measureup.com/microsoft-practice-test-az-900-microsoft-azure-fundamentals.html"
              price="From £79"
              badgeText="Official Microsoft Partner"
            />
            <AffiliateCTA
              programme="Whizlabs"
              courseName="AZ-900 Practice Tests"
              url="https://www.whizlabs.com/microsoft-azure-certification-az-900/"
              price="From £12"
              badgeText="Budget Option"
            />
          </div>
        </section>

        {/* SECTION 7 */}
        <section id="study-plan">
          <h2>Recommended Study Plan</h2>
          <p>
            AZ-900 is achievable in two weeks studying one to two hours per day part-time. The
            exam covers breadth rather than depth, so a focused two-week pass is more effective
            than a long drawn-out study period.
          </p>
          <div className="not-prose flex flex-col gap-4 my-6">
            {/* Week 1 */}
            <div className="border rounded p-5" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
              <p className="text-[13px] font-bold text-[#0078d4] uppercase tracking-wide mb-3">
                Week 1 — Core Concepts and Architecture
              </p>
              <div className="flex flex-col gap-3">
                {[
                  {
                    days: 'Days 1–2',
                    title: 'Cloud concepts (Domain 1)',
                    detail: 'Complete Microsoft Learn Module 1 — IaaS/PaaS/SaaS, shared responsibility',
                  },
                  {
                    days: 'Days 3–4',
                    title: 'Azure compute and storage',
                    detail: 'VMs, Blob Storage, App Service, Azure Functions',
                  },
                  {
                    days: 'Days 5–7',
                    title: 'Azure networking and identity',
                    detail: 'VNet, Load Balancer, Microsoft Entra ID, RBAC',
                  },
                ].map((w) => (
                  <div key={w.days} className="flex gap-3">
                    <span className="text-[11px] font-bold w-16 shrink-0 pt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                      {w.days}
                    </span>
                    <div>
                      <p className="text-[13px] font-semibold" style={{ color: 'var(--text-secondary)' }}>{w.title}</p>
                      <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{w.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Week 2 */}
            <div className="border rounded p-5" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
              <p className="text-[13px] font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--text-secondary)' }}>
                Week 2 — Governance and Exam Prep
              </p>
              <div className="flex flex-col gap-3">
                {[
                  {
                    days: 'Days 8–9',
                    title: 'Azure management and governance (Domain 3)',
                    detail: 'Cost management, Azure Policy, SLAs, support plans',
                  },
                  {
                    days: 'Days 10–11',
                    title: 'Free Microsoft Learn practice assessment',
                    detail: '50 questions — free, identifies weak areas',
                  },
                  {
                    days: 'Days 12–13',
                    title: 'MeasureUp or Whizlabs practice tests',
                    detail: 'Timed, exam-format questions to build confidence',
                  },
                  {
                    days: 'Day 14',
                    title: 'Review weak areas and book exam',
                    detail: 'Go back to Microsoft Learn on topics where you scored low',
                  },
                ].map((w) => (
                  <div key={w.days} className="flex gap-3">
                    <span className="text-[11px] font-bold w-16 shrink-0 pt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                      {w.days}
                    </span>
                    <div>
                      <p className="text-[13px] font-semibold" style={{ color: 'var(--text-secondary)' }}>{w.title}</p>
                      <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{w.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8 */}
        <section id="pricing-exam">
          <h2>Azure Pricing in the AZ-900 Exam</h2>
          <p>
            Domain 3 includes Azure pricing and cost management — and it is tested in more detail
            than many candidates expect. The exam covers:
          </p>
          <ul>
            <li>Total Cost of Ownership (TCO) and the Azure TCO calculator</li>
            <li>Azure pricing models: pay-as-you-go, reserved instances, spot instances</li>
            <li>Cost-saving features: Azure Hybrid Benefit, reserved capacity</li>
            <li>Azure Cost Management + Billing</li>
          </ul>
          <p>
            The best way to understand Azure pricing is to use the actual pricing calculators for
            the services most commonly featured in AZ-900 exam questions. AzureCalc.uk covers UK
            South pricing for Blob Storage, Azure Functions, and App Service — three services that
            appear regularly in exam scenarios:
          </p>
          <div className="not-prose flex flex-col gap-3 my-6">
            <CalculatorEmbed service="blob-storage" />
            <CalculatorEmbed service="functions" />
            <CalculatorEmbed service="app-service" />
          </div>
        </section>

        {/* SECTION 9 */}
        <section id="after-az-900">
          <h2>After AZ-900 — What Next?</h2>
          <p>
            AZ-900 is a foundation, not a destination. Once you hold AZ-900, the right next
            certification depends on your role:
          </p>
          <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
            {[
              {
                role: 'Operations / Administration',
                cert: 'AZ-104 Azure Administrator',
                detail: 'Managing subscriptions, storage, VMs, networking, and identity',
                path: null,
              },
              {
                role: 'Development',
                cert: 'AZ-204 Azure Developer Associate',
                detail: 'Building and deploying Azure applications',
                path: null,
              },
              {
                role: 'Security (SOC / Threat)',
                cert: 'SC-200 Security Operations Analyst',
                detail: 'Microsoft Sentinel, Defender XDR, threat hunting',
                path: '/certifications/sc-200-study-guide-uk',
              },
              {
                role: 'Security (Engineering)',
                cert: 'AZ-500 Azure Security Engineer',
                detail: 'Implementing security controls across Azure infrastructure',
                path: '/certifications/az-500-study-guide-uk',
              },
              {
                role: 'Architecture',
                cert: 'AZ-305 Azure Solutions Architect',
                detail: 'Designing cloud solutions, identity, data, and infrastructure',
                path: null,
              },
            ].map((item) => (
              <div
                key={item.cert}
                className="border rounded p-4 flex flex-col gap-1"
                style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}
              >
                <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
                  {item.role}
                </p>
                {item.path ? (
                  <Link
                    to={item.path}
                    className="text-[14px] font-semibold text-[#0078d4] hover:underline"
                  >
                    {item.cert} →
                  </Link>
                ) : (
                  <p className="text-[14px] font-semibold" style={{ color: 'var(--text-secondary)' }}>{item.cert}</p>
                )}
                <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{item.detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SECTION 10 */}
        <section id="related-resources">
          <h2>Related Resources</h2>
          <div className="not-prose flex flex-col gap-2 my-4">
            <ResourceLink
              title="Official AZ-900 certification page"
              url="https://learn.microsoft.com/en-us/credentials/certifications/azure-fundamentals/"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="AZ-900 free practice assessment (50 questions)"
              url="https://learn.microsoft.com/en-us/credentials/certifications/azure-fundamentals/practice/assessment"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="AZ-900 study guide PDF"
              url="https://learn.microsoft.com/en-us/credentials/certifications/resources/study-guides/az-900"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="SC-200 Study Guide UK"
              url="/certifications/sc-200-study-guide-uk"
              source="AzureCalc.uk"
            />
          </div>
        </section>
      </CertificationLayout>
    </>
  );
}
